import React, { useState } from 'react';
import { Space, Table, Tag, Button, Image, Card, Input, Flex, Select, Modal, Dropdown } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import TagsPicker from "components/pickers/product_tag"
import CategoryPicker from "components/pickers/product_category"
import ProductInventoryModal from '../ProductInventoryModal';
import { useWorkSpace } from 'contexts/workspace';
import { useApp } from 'contexts/app';

const { Search } = Input;

const ProductListDesktop = ({ viewModel }) => {
    const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [variantModalProduct, setVariantModalProduct] = useState(null);
    const { workspace } = useWorkSpace();
    const { message } = useApp();
    const productLimitStatus = workspace?.subscriptionStatus?.canCreateProduct;

    const handleCreateProduct = () => {
      if (productLimitStatus?.canCreate === false) {
        setLimitModalOpen(true);
        return;
      }
      viewModel.navigate('/product/add');
    };

  const openVariantModal = (record) => {
    setVariantModalProduct(record);
    setVariantModalOpen(true);
  };

  const handleViewProductWebsite = (record) => {
    const productID = String(record?.id || '').trim();
    if (!productID) {
      message.error('Sản phẩm chưa có id để xem trên website');
      return;
    }

    const websiteBaseUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');
    if (!websiteBaseUrl) {
      message.error('Chưa cấu hình website shop để xem sản phẩm');
      return;
    }

    window.open(`${websiteBaseUrl}/product/${encodeURIComponent(productID)}`, '_blank', 'noopener,noreferrer');
  };

  const {
    data,
    filter,
    selectedProductIds,
    expandedInventoryRows,
    bulkTagModalOpen,
    bulkRemoveTagModalOpen,
    bulkActionLoading,
    bulkTagIds,
    bulkRemoveTagIds,
    bulkTagSaving,
    bulkRemoveTagSaving,
    inventoryModalOpen,
    inventoryProduct,
    inventoryRows,
    inventorySaving,
    inventoryImporting,
    inventoryImportPreviewOpen,
    inventoryImportPreviewRows,
    inventoryImportUnmatchedIds,
    inventoryImportSaving,
    setFilterValue,
    navigate,
    openBulkTagModal,
    saveBulkTags,
    openBulkRemoveTagModal,
    saveBulkRemoveTags,
    onBulkActionSelect,
    getProductTags,
    getProductCategories,
    getProductOptionSummary,
    getSortedVariantsByStock,
    getVariantDisplayName,
    openInventoryModal,
    onVariantStockChange,
    saveInventory,
    importInventoryFromFile,
    setExpandedInventoryRows,
    setInventoryModalOpen,
    setBulkTagIds,
    setBulkTagModalOpen,
    setBulkRemoveTagIds,
    setBulkRemoveTagModalOpen,
    setInventoryImportPreviewOpen,
    setSelectedProductIds,
    applyImportedInventory,
  } = viewModel;

  const columns = [
    {
      title: 'Sản phẩm',
      width: 420,
      render: (_, record) => {
        const productTags = getProductTags(record);
        const productCategories = getProductCategories(record);

        return (
          <div>
            <Space align='start'>
              <Image width={75} src={record.thumbnail.url} />
              <div>
                <div>{record.name}</div>
                <Space direction='vertical' size={4} style={{ marginTop: 4 }}>
                  <div>Tag: {productTags.length ? productTags.join(', ') : '-'}</div>
                  <div>Danh mục: {productCategories.length ? productCategories.join(', ') : '-'}</div>
                </Space>
              </div>
            </Space>
          </div>
        )
      }
    },
    {
      title: 'Biến thể',
      width: 300,
      render: (_, record) => {
        const optionSummary = getProductOptionSummary(record);

        return (
          <div style={{ maxWidth: 280, cursor: optionSummary.length ? 'pointer' : 'default' }} onClick={() => optionSummary.length && openVariantModal(record)}>
            <Space direction='vertical' size={0} style={{ width: '100%' }}>
              {optionSummary.map((line, index) => (
                <div
                  key={`${record?.id || 'product'}-opt-${index}`}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={line}
                >
                  {line}
                </div>
              ))}
              {optionSummary.length > 0 && (
                <Button type='link' size='small' style={{ padding: 0, height: 'auto', textAlign: 'left' }}>
                  Xem tất cả biến thể
                </Button>
              )}
            </Space>
          </div>
        )
      }
    },
    {
      title: 'Tồn kho',
      width: 260,
      render: (value, record) => {
        if (!record.track_inventory) return null;
        const variants = getSortedVariantsByStock(record);
        const isExpanded = !!expandedInventoryRows[record.id];
        const visibleVariants = isExpanded ? variants : variants.slice(0, 3);

        return (
          <Space direction='vertical' size={0}>
            {visibleVariants.map(variant => {
              const variantName = getVariantDisplayName(variant);
              const onHand = Number(variant?.on_hand_quantity ?? 0) || 0;
              const stockText = onHand === 0 ? 'Hết hàng' : onHand;
              return <div key={variant.id}>{variantName}: {stockText}</div>
            })}
            {variants.length > 3 && (
              <Button
                type='link'
                style={{ padding: 0 }}
                onClick={() => setExpandedInventoryRows(prev => ({ ...prev, [record.id]: !isExpanded }))}
              >
                {isExpanded ? 'Thu gọn' : 'Xem thêm'}
              </Button>
            )}
            <Button type='link' style={{ padding: 0 }} onClick={() => openInventoryModal(record)}>Cập nhật tồn</Button>
          </Space>
        )
      }
    },
    {
      title: "",
      key: 'action',
      width: 128,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type='link' onClick={() => handleViewProductWebsite(record)}><EyeOutlined /></Button>
          <Button type='link' onClick={e => navigate(`/product/edit/${record.id}`)}><EditOutlined /></Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card style={{ width: "100%" }}>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Search
            placeholder="Tên hoặc mã"
            onChange={e => setFilterValue("info", e.target.value)}
            style={{
              width: "100%",
            }}
            value={filter.info}
          />
          <Space size={16}>
            <div style={{ gap: 8 }}>
              <label>Kho </label>
              <Select defaultValue={"all"} value={filter.stock} style={{ width: 200 }} onChange={e => setFilterValue("stock", e)}>
                <Select.Option value="all">Tất cả</Select.Option>
                <Select.Option value="in_stock">Còn</Select.Option>
                <Select.Option value="low_stock">Còn ít</Select.Option>
                <Select.Option value="out_of_stock">Hết</Select.Option>
              </Select>
            </div>
            <div>
              <label>Hiện trên website </label>
              <Select defaultValue={"all"} value={filter.status} style={{ width: 200 }} onChange={e => setFilterValue("status", e)}>
                <Select.Option value="all">Tất cả</Select.Option>
                <Select.Option value="show">Đang hiển thị</Select.Option>
                <Select.Option value="hide">Đang ẩn</Select.Option>
              </Select>
            </div>
            <div>
              <label>Tag </label>
              <TagsPicker value={filter.tag} style={{ width: 200 }} onChange={e => setFilterValue("tag", e)} ></TagsPicker>
            </div>
            <div>
              <label>Danh mục </label>
              <CategoryPicker value={filter.category} style={{ width: 200 }} onChange={e => setFilterValue("category", e)} ></CategoryPicker>
            </div>
          </Space>

          <Flex justify='flex-end' style={{ width: "100%" }}>
            <Button 
              type='primary' 
              onClick={handleCreateProduct}
              title={productLimitStatus?.canCreate === false ? `Đã đạt giới hạn ${productLimitStatus?.limit} sản phẩm` : ''}
            >
              Thêm mới
            </Button>
          </Flex>
        </Space>
      </Card>

      <Card>
        <Flex justify='space-between' align='center' style={{ marginBottom: 12 }}>
          <div>{selectedProductIds.length > 0 ? `Đã chọn: ${selectedProductIds.length}` : null}</div>
          <Space>
            <Dropdown.Button
              type='primary'
              disabled={selectedProductIds.length === 0}
              loading={bulkActionLoading}
              onClick={openBulkTagModal}
              menu={{
                items: [
                  { key: 'tag', label: 'Gắn tag' },
                  { key: 'remove_tag', label: 'Gỡ tag' },
                  { key: 'export_inventory', label: 'Xuất file kho hàng' },
                  { key: 'hide', label: 'Ẩn trên website' },
                  { key: 'show', label: 'Hiện trên website' }
                ],
                onClick: onBulkActionSelect
              }}
            >
              Chức năng
            </Dropdown.Button>
          </Space>
        </Flex>
        <Table
          rowKey='id'
          rowSelection={{
            selectedRowKeys: selectedProductIds,
            onChange: (keys) => setSelectedProductIds(keys)
          }}
          columns={columns}
          dataSource={data}
          scroll={{ x: 1200 }}
        />
      </Card>

      <ProductInventoryModal
        open={inventoryModalOpen}
        product={inventoryProduct}
        rows={inventoryRows}
        saving={inventorySaving}
        importLoading={inventoryImporting}
        onCancel={() => setInventoryModalOpen(false)}
        onSave={saveInventory}
        onImportFromFile={importInventoryFromFile}
        onVariantStockChange={onVariantStockChange}
        getVariantDisplayName={getVariantDisplayName}
      />

      <Modal
        title={`Dữ liệu import tồn kho - ${inventoryProduct?.name || ''}`}
        open={inventoryImportPreviewOpen}
        onCancel={() => setInventoryImportPreviewOpen(false)}
        onOk={applyImportedInventory}
        okText='Cập nhật tồn kho'
        cancelText='Đóng'
        confirmLoading={inventoryImportSaving}
        width={1000}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <div>
            File có {inventoryImportPreviewRows.length} dòng dữ liệu.
            {inventoryImportUnmatchedIds.length > 0 ? ` Có ${inventoryImportUnmatchedIds.length} dòng có variant_id không tồn tại trong hệ thống.` : ''}
          </div>
          <Table
            pagination={false}
            rowKey='key'
            dataSource={inventoryImportPreviewRows}
            rowClassName={row => row.matched ? '' : 'inventory-import-row-unmatched'}
            columns={[
              {
                title: 'Dòng',
                render: (_, row) => Number(row.row_index ?? 0)
              },
              {
                title: 'variant_id',
                dataIndex: 'variant_id'
              },
              {
                title: 'Biến thể',
                dataIndex: 'variant_title'
              },
              {
                title: 'Trạng thái',
                render: (_, row) => row.matched
                  ? <Tag color='success'>Khớp</Tag>
                  : <Tag color='error'>Không khớp</Tag>
              },
              {
                title: 'Tồn hiện tại',
                render: (_, row) => row.matched ? Number(row.current_on_hand_quantity ?? 0) : '-'
              },
              {
                title: 'Tồn từ file',
                render: (_, row) => Number(row.next_on_hand_quantity ?? 0)
              },
              {
                title: 'Đã đặt',
                render: (_, row) => row.matched ? Number(row.reserved_quantity ?? 0) : '-'
              },
              {
                title: 'Khả dụng mới',
                render: (_, row) => row.matched ? Number(row.next_available_quantity ?? 0) : '-'
              }
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title='Gắn tag hàng loạt'
        open={bulkTagModalOpen}
        onCancel={() => setBulkTagModalOpen(false)}
        onOk={saveBulkTags}
        okText='Lưu'
        cancelText='Đóng'
        confirmLoading={bulkTagSaving}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <div>Chọn tag để gắn cho {selectedProductIds.length} sản phẩm đã chọn</div>
          <TagsPicker
            mode='multiple'
            value={bulkTagIds}
            onChange={setBulkTagIds}
            style={{ width: '100%' }}
            placeholder='Chọn tag'
          />
        </Space>
      </Modal>

      <Modal
        title='Gỡ tag hàng loạt'
        open={bulkRemoveTagModalOpen}
        onCancel={() => setBulkRemoveTagModalOpen(false)}
        onOk={saveBulkRemoveTags}
        okText='Lưu'
        cancelText='Đóng'
        confirmLoading={bulkRemoveTagSaving}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <div>Chọn tag để gỡ khỏi {selectedProductIds.length} sản phẩm đã chọn</div>
          <TagsPicker
            mode='multiple'
            value={bulkRemoveTagIds}
            onChange={setBulkRemoveTagIds}
            style={{ width: '100%' }}
            placeholder='Chọn tag'
          />
        </Space>
      </Modal>

      <Modal
        title='Giới hạn sản phẩm'
        open={limitModalOpen}
        onCancel={() => setLimitModalOpen(false)}
        footer={[
          <Button key='cancel' onClick={() => setLimitModalOpen(false)}>
            Đóng
          </Button>,
          <Button 
            key='upgrade' 
            type='primary' 
            onClick={() => {
              setLimitModalOpen(false);
              navigate('/account/subscription');
            }}
          >
            Nâng cấp gói
          </Button>,
        ]}
      >
        <p>
          Bạn đã đạt giới hạn <strong>{productLimitStatus?.limit}</strong> sản phẩm.
        </p>
        <p>
          Để có thể thêm sản phẩm, vui lòng nâng cấp gói cước của bạn.
        </p>
      </Modal>

      <Modal
        title={`Biến thể sản phẩm - ${variantModalProduct?.name || ''}`}
        open={variantModalOpen}
        onCancel={() => {
          setVariantModalOpen(false);
          setVariantModalProduct(null);
        }}
        footer={[
          <Button key='close' onClick={() => {
            setVariantModalOpen(false);
            setVariantModalProduct(null);
          }}>
            Đóng
          </Button>
        ]}
      >
        <Space direction='vertical' size={8} style={{ width: '100%' }}>
          {(getProductOptionSummary(variantModalProduct) || []).map((line, index) => (
            <div key={`variant-full-${index}`} style={{ wordBreak: 'break-word' }}>{line}</div>
          ))}
        </Space>
      </Modal>
    </Space>
  );
};

export default ProductListDesktop;

